import { useState } from "react";
import { CreditCard, HelpCircle, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

interface AddStepDialogProps {
  open: boolean;
  onClose: () => void;
  templates: { id: string; code: string; label: string; is_interactive?: boolean }[];
  onAddCard: (templateId: string) => void;
  onAddGroup: (groupType: string) => void;
}

export default function AddStepDialog({ open, onClose, templates, onAddCard, onAddGroup }: AddStepDialogProps) {
  const [mode, setMode] = useState<"choose" | "card">("choose");
  const [selectedTemplate, setSelectedTemplate] = useState("");

  const reset = () => {
    setMode("choose");
    setSelectedTemplate("");
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          onClose();
          reset();
        }
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{mode === "choose" ? "Ajouter une étape" : "Choisir un template de carte"}</DialogTitle>
        </DialogHeader>

        {mode === "choose" && (
          <div className="grid grid-cols-3 gap-3">
            <button
              className="flex flex-col items-center gap-2 rounded-lg border border-border p-6 transition-colors hover:bg-accent/50"
              onClick={() => setMode("card")}
            >
              <CreditCard className="h-8 w-8 text-primary" />
              <span className="font-medium">Carte</span>
              <span className="text-xs text-muted-foreground text-center">Une carte individuelle</span>
            </button>
            <button
              className="flex flex-col items-center gap-2 rounded-lg border border-border p-6 transition-colors hover:bg-accent/50"
              onClick={() => {
                onAddGroup("quiz");
                reset();
              }}
            >
              <HelpCircle className="h-8 w-8 text-primary" />
              <span className="font-medium">Quiz</span>
              <span className="text-xs text-muted-foreground text-center">Un groupe de cartes de type quiz</span>
            </button>
            <button
              className="flex flex-col items-center gap-2 rounded-lg border border-border p-6 transition-colors hover:bg-accent/50"
              onClick={() => {
                onAddGroup("action");
                reset();
              }}
            >
              <Zap className="h-8 w-8 text-primary" />
              <span className="font-medium">Action</span>
              <span className="text-xs text-muted-foreground text-center">Un groupe de cartes de type action</span>
            </button>
          </div>
        )}

        {mode === "card" && (
          <div className="space-y-3">
            <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un template..." />
              </SelectTrigger>
              <SelectContent>
                {templates.filter((t) => !t.is_interactive).length > 0 && (
                  <SelectGroup>
                    <SelectLabel>Non interactives</SelectLabel>
                    {templates
                      .filter((t) => !t.is_interactive)
                      .map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.label}
                        </SelectItem>
                      ))}
                  </SelectGroup>
                )}
                {templates.filter((t) => t.is_interactive).length > 0 && (
                  <SelectGroup>
                    <SelectLabel>Interactives</SelectLabel>
                    {templates
                      .filter((t) => t.is_interactive)
                      .map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.label}
                        </SelectItem>
                      ))}
                  </SelectGroup>
                )}
              </SelectContent>
            </Select>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMode("choose")}>
                Retour
              </Button>
              <Button
                disabled={!selectedTemplate}
                onClick={() => {
                  onAddCard(selectedTemplate);
                  reset();
                }}
              >
                Ajouter
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
